import { Headline, Text } from '@velocity/ui'
import * as React from 'react'
import { useEffect, useRef } from 'react'

import { Feature, type StepLayoutProps } from '@ngb-frontend/shared/types'

import { useStepLayoutStyles } from './StepLayout.styled'
import { BrandNavigationBar } from '../BrandNavigationBar/BrandNavigationBar'
import { FlexColumn } from '../FlexBox/FlexColumn'
import { Footer } from '../Footer/Footer'
import { NavigationBar } from '../NavigationBar/NavigationBar'
import {
  StepFooterProvider,
  useStepTakeover,
} from '../StepNavigationFooter/StepFooterContext'
import { StepNavigationFooter } from '../StepNavigationFooter/StepNavigationFooter'
import { AppStepper } from '../Stepper/Stepper'

// Will be moved to shared/config in followup story (WIP)
const DRIVER_STEPPER_SECTIONS = [
  'stepperSections.vehicle',
  'stepperSections.serviceSelection',
  'stepperSections.appointment',
  'stepperSections.summary',
]

const StepLayoutFrame: React.FC<StepLayoutProps> = ({
  navLabel,
  stepperIndex,
  title,
  subtitle,
  progress,
  onStepBack,
  onClose,
  confirmClose,
  children,
  topNavLabel,
  showLogo,
  fullWidth = false,
  narrow = false,
  type = Feature.MnR,
  footer,
  navigation = 'steps',
}) => {
  const compact = type === Feature.MnR
  // Driver journey steps get the sticky Previous/Next navigation footer;
  // every other page/persona keeps the informational links footer.
  const showNavigationFooter = type === Feature.MnR && !!footer
  // A step state that stands in for the step (an error screen): no stepper,
  // no footer, and its own way back
  const takeover = useStepTakeover()
  const isTakenOver = !!takeover

  const classes = useStepLayoutStyles({
    fullWidth,
    narrow,
    showLogo: showLogo && !compact,
  })

  const containerRef = useRef<HTMLDivElement>(null)

  // The container owns the scrolling (overflow: auto) and its DOM node is
  // reused across step navigations, so the previous step's scroll offset
  // would leak into the next step and open it scrolled halfway down. Next.js
  // only resets the window scroll on route changes, so reset the container
  // manually whenever the rendered step changes.
  useEffect(() => {
    // scrollTop assignment instead of scrollTo(): jsdom implements the
    // former but not the latter, and unit tests render this on every step.
    if (containerRef.current) containerRef.current.scrollTop = 0
  }, [navLabel, stepperIndex, title, isTakenOver])

  return (
    <FlexColumn className={classes.root}>
      {navigation === 'brand' ? (
        <BrandNavigationBar />
      ) : (
        <NavigationBar
          compact={compact}
          label={navLabel}
          onStepBack={takeover?.onStepBack || onStepBack}
          onClose={onClose}
          confirmClose={confirmClose}
          progress={progress}
          topLabel={topNavLabel}
          showLogo={showLogo}
        />
      )}
      <div className={classes.container} ref={containerRef}>
        {stepperIndex !== undefined && !isTakenOver && (
          <AppStepper
            steps={DRIVER_STEPPER_SECTIONS}
            activeStep={stepperIndex}
          />
        )}
        <div className={classes.content}>
          <div className={classes.inner}>
            {title && !isTakenOver && (
              <Headline
                variant="300"
                className={classes.title}
                data-e2e-component="step-layout-title"
              >
                {title}
              </Headline>
            )}
            {subtitle && !isTakenOver && (
              <Text
                variant="200"
                className={classes.subtitle}
                data-e2e-component="step-layout-subtitle"
              >
                {subtitle}
              </Text>
            )}
            {children}
          </div>
        </div>
        {!showNavigationFooter ? (
          <Footer />
        ) : (
          !isTakenOver && (
            <StepNavigationFooter {...footer} onPrevious={onStepBack} />
          )
        )}
      </div>
    </FlexColumn>
  )
}

export const StepLayout: React.FC<StepLayoutProps> = (props) => (
  <StepFooterProvider>
    <StepLayoutFrame {...props} />
  </StepFooterProvider>
)
